/* eslint-disable react-hooks/set-state-in-effect */
import { ReactNode, useState, useEffect } from 'react';
import { BannerExpandableAlertBox, SmallAlertBox } from '@entur/alert';
import { useIntl } from 'react-intl';
import { VariantType } from '@entur/form';
import styles from './styles.module.scss';

interface ErrorMessageProps {
  error?: string | null;
  httpCode?: number | null;
  value?: ReactNode;
  header?: string;
  variant?: VariantType;
  small?: boolean;
  clickable?: boolean;
  onClick?: () => void;
  dataCy?: string;
}

const ErrorMessage = ({
  error,
  httpCode = null,
  value,
  header,
  variant,
  small = false,
  clickable = false,
  onClick,
  dataCy,
}: ErrorMessageProps) => {
  const intl = useIntl();
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('');
  const [type, setType] = useState(variant || 'error');
  const correlationId = error
    ? error.match(/[A-Fa-f0-9]{8}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{4}-[A-Fa-f0-9]{12}/g)
    : null;

  useEffect(() => {
    if (error && httpCode) {
      if (httpCode === 400) {
        setType('warning');
        setTitle(intl.formatMessage({ id: 'warnings.warning400' }));
        setMessage(intl.formatMessage({ id: 'warnings.warningMessage400' }));
      } else if (httpCode === 404) {
        setType('warning');
        setTitle(intl.formatMessage({ id: 'warnings.warning404' }));
        setMessage(intl.formatMessage({ id: 'warnings.warningMessage404' }));
      } else if (httpCode === 403) {
        setType('warning');
        setTitle(intl.formatMessage({ id: 'warnings.warning403' }));
        setMessage(intl.formatMessage({ id: 'warnings.warningMessage403' }));
      } else if (httpCode === 409) {
        setType('warning');
        setTitle(intl.formatMessage({ id: 'warnings.warning409' }));
        setMessage(`${intl.formatMessage({ id: 'warnings.warningMessage409' })}${correlationId}`);
      } else if (httpCode > 400 && httpCode < 500) {
        setType('warning');
        setTitle(intl.formatMessage({ id: 'warnings.warning400Default' }));
        setMessage(intl.formatMessage({ id: 'warnings.warningMessage400Default' }));
      } else if (httpCode === 504) {
        setTitle(intl.formatMessage({ id: 'error.error504' }));
        setMessage(intl.formatMessage({ id: 'error.errorMessage504' }));
      } else if (httpCode > 499 && httpCode < 600) {
        setTitle(intl.formatMessage({ id: 'error.error500Default' }));
        setMessage(`${intl.formatMessage({ id: 'error.errorMessage504Default' })}${correlationId}`);
      } else {
        setTitle(intl.formatMessage({ id: 'error.defaultError' }));
      }
    }
  }, [error, httpCode, intl, correlationId]);

  if (small) {
    return (
      <SmallAlertBox
        data-cy={dataCy}
        variant={type}
        onClick={() => (onClick ? onClick() : null)}
        className={clickable ? styles.clickable : undefined}
        style={{ wordBreak: 'break-word', marginBottom: '1em' }}
      >
        {value}
      </SmallAlertBox>
    );
  }
  return (
    <>
      <b />
      <BannerExpandableAlertBox
        data-cy={dataCy}
        variant={type}
        title={title || header}
        style={{ marginBottom: '1em' }}
        className={styles.breakLine}
      >
        {message || null}
        <br />
        <br />
        {error || value || null}
      </BannerExpandableAlertBox>
    </>
  );
};

export default ErrorMessage;
